import { createSlice } from '@reduxjs/toolkit';

import type { ColorPaletteName } from '../config/theme';

/**
 * The same four modes and palettes as the VictorIA UI, so the two apps can be
 * set up alike. Replaces the shared core's slice, which has no palette.
 */
export type ThemeMode = 'light' | 'dark' | 'system' | 'daytime';

export interface PreferencesState {
    theme: ThemeMode;
    colorPalette: ColorPaletteName;
}

const THEME_MODES: ThemeMode[] = ['light', 'dark', 'system', 'daytime'];

const readStorage = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeStorage = (key: string, value: string) => {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Private mode or blocked storage: the preference lasts for the session.
    }
};

const getInitialTheme = (): ThemeMode => {
    const stored = readStorage('theme') as ThemeMode | null;
    // An admin console defaults to dark.
    return stored && THEME_MODES.includes(stored) ? stored : 'dark';
};

const getInitialState = (): PreferencesState => ({
    theme: getInitialTheme(),
    colorPalette:
        (readStorage('colorPalette') as ColorPaletteName | null) || 'default',
});

/** Resolves 'system' and 'daytime' to the palette actually rendered. */
export const resolveThemeMode = (
    mode: ThemeMode,
    now: Date = new Date()
): 'light' | 'dark' => {
    switch (mode) {
        case 'system':
            return window.matchMedia?.('(prefers-color-scheme: dark)').matches
                ? 'dark'
                : 'light';
        case 'daytime': {
            const hour = now.getHours();
            return hour >= 7 && hour < 19 ? 'light' : 'dark';
        }
        default:
            return mode;
    }
};

export const preferencesInitialState = getInitialState();

const preferencesSlice = createSlice({
    name: 'preferences',
    initialState: getInitialState,
    reducers: {
        setTheme: (state, action: { payload: ThemeMode }) => {
            state.theme = action.payload;
            writeStorage('theme', state.theme);
        },
        setColorPalette: (state, action: { payload: ColorPaletteName }) => {
            state.colorPalette = action.payload;
            writeStorage('colorPalette', action.payload);
        },
    },
});

export const { setTheme, setColorPalette } = preferencesSlice.actions;
export const preferencesReducer = preferencesSlice.reducer;
export default preferencesSlice;
